// Who is signed in to this instance, and what they may reach.
//
// A login is NOT a Solid identity. The institution has exactly one
// institutional WebID — the identity partners grant access to, held by the
// backend. Staff sign in to this instance with their own email and password
// (lib/accounts.js); the backend keeps the session (an HttpOnly cookie the
// browser can't read) and enforces what the login's roles permit. This module
// only mirrors that for navigation.

import { SELF } from '../../shared/config.js';
import { getContext, getInstitution, getMe, login, logout } from './api.js';

export const session = $state({
  user: null, // { id, email, name, roles, ... } or null when signed out
  permissions: [],
  screens: [],
  context: null, // { self, institution, partners, roles } from the backend
  institution: null, // { name, label, tagline, accent } — Pod-resident brand
  health: null, // { ok, actingAs } — proves the held credential works
  error: null,
});

// Presentation identity comes from the Pod (institution.json). When it's
// absent — a Pod without the file, or the backend unreachable — fall back to
// the slug so the app renders rather than breaking.
const fallbackInstitution = () => ({
  name: SELF.institution, label: SELF.institution, tagline: '', accent: null,
});

export const institution = () => session.institution ?? fallbackInstitution();

// Apply the Pod-resident identity: the brand + tagline + document.title, and
// the accent colour as a root CSS custom property (overriding the theme
// default). Editing institution.json in the Pod changes all of these on reload,
// with no rebuild.
export function applyInstitution(inst) {
  inst ??= fallbackInstitution();
  session.institution = inst;
  if (inst.name) document.title = inst.tagline ? `${inst.name} — ${inst.tagline}` : inst.name;
  if (inst.accent) document.documentElement.style.setProperty('--accent', inst.accent);
}

export function applyContext(ctx) {
  session.context = ctx;
  if (ctx?.self) Object.assign(SELF, ctx.self);
  if (ctx?.institution) applyInstitution(ctx.institution);
}

function applyMe(me) {
  session.user = me?.user ?? null;
  session.permissions = me?.permissions ?? [];
  session.screens = me?.screens ?? [];
}

// The sign-in screen needs the brand before anyone is signed in; everything
// else in the context waits for a session.
export async function restore() {
  const [inst, me] = await Promise.allSettled([getInstitution(), getMe()]);
  applyInstitution(inst.status === 'fulfilled' ? inst.value : null);
  applyMe(me.status === 'fulfilled' ? me.value : null);
  if (session.user) await loadContext();
}

async function loadContext() {
  try { applyContext(await getContext()); } catch { /* brand already applied */ }
}

export async function signIn(email, password) {
  applyMe(await login(email, password));
  await loadContext();
}

export async function signOut() {
  await logout().catch(() => {});
  applyMe(null);
}

// Any API call answered 401 means the session is gone (expired, signed out
// elsewhere, login disabled); the app gate then shows the sign-in screen.
export function sessionEnded() {
  applyMe(null);
}

export const can = (permission) => session.permissions.includes(permission);

export const roleLabels = () =>
  (session.user?.roles ?? []).map((id) => session.context?.roles?.[id]?.label ?? id);

// May the signed-in login reach this route? Roles are about scope of work, not
// secrecy — the Pod's own WAC is the real boundary — so this shapes navigation
// rather than pretending to be a security control. Every act that writes is
// enforced in the backend too (web/server/policy.js).
export function mayVisit(route) {
  route = String(route).split('?')[0];
  if (!session.user) return false;
  // What the institution has is every login's to see; what it can do there
  // still depends on the roles.
  if (['overview', 'login', 'account', 'data/dataset'].includes(route)) return true;
  return session.screens.includes(route);
}
